import { useState } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'
import { canSeeAdmin } from '../../auth/adminAccess'
import { AboutButton } from '@versports/ui'
import { AppNavBar } from '../../components/AppNavBar'
import { VerSportsLogo } from '@versports/ui'
import { ImpersonationBanner } from '../../components/ImpersonationBanner'

/**
 * Shell for the /data module — the player's PERSONAL area (perfil + info
 * agregada). Ya no es el catálogo de auto-servicio: la asignación de
 * cuestionarios la maneja el club/manager/admin vía invitación, y el gate
 * de cuestionarios pendientes corre adentro del vr-app después de
 * "Comenzar" (ver `pending_questionnaires` en /auth/me). Acá quedan los
 * items personales; el form de cada cuestionario (`/data/:code`) sigue
 * funcionando standalone.
 *
 * Desde 2026-09-07 el layout NO tiene tab strip: `/data` es una sola página
 * scrolleable (`KnowHome`: perfil → ranking → performance → cuestionarios →
 * sesiones → señales) y las rutas hermanas que quedan son destinos puntuales
 * (`/data/profile` el wizard de edición, `/data/:code` el form),
 * alcanzables desde links del header de `KnowHome`. `/data/pre-session` ya no
 * existe: el chequeo previo es obligatorio por sesión y se responde en el
 * casco (gate del vr-app). `/data/performance` y `/data/physiology` redirigen al índice.
 *
 * Zonas:
 *   - topbar con brand + identity strip
 *   - main content area: <Outlet />
 *   - global AboutButton (modal "Acerca de") + AppNavBar (nav inferior global)
 *
 * `setFullscreen` viaja por outlet context para que un hijo (hoy sólo
 * `KnowProfile` durante el onboarding) pueda pedir modo full-screen: oculta
 * el header para que el wizard ocupe toda la pantalla, sin duplicar el chrome
 * de navegación mientras dura ese flujo dedicado.
 *
 * `revalidate` se mantiene en el outlet context para que
 * `KnowQuestionnaire` pueda dispararlo tras el submit sin romper su
 * plumbing; hoy no hay catálogo que refrescar (el gate lo limpia `refresh()`
 * de AuthContext), así que es un no-op.
 */
export function KnowLayout() {
  const { user, logout } = useAuth()
  const location = useLocation()
  const [fullscreen, setFullscreen] = useState(false)

  const revalidate = async () => {}

  const isAdmin = canSeeAdmin(user)
  const displayName = user?.profile?.display_name || user?.username || ''

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <ImpersonationBanner />
      {!fullscreen && (
        <header className="border-b-2 border-primary bg-surface shadow-[0_12px_36px_rgba(226,90,27,0.12)]">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-lg py-md">
            <Link to="/" className="no-underline">
              <VerSportsLogo />
            </Link>
            <div className="flex items-center gap-md text-sm">
              {isAdmin && (
                <Link to="/admin" className="tag-ok no-underline">
                  admin
                </Link>
              )}
              <span className="text-text-muted">{displayName}</span>
              <button
                type="button"
                onClick={() => void logout()}
                className="btn-ghost px-md py-xs text-xs"
              >
                Salir
              </button>
            </div>
          </div>
        </header>
      )}

      <div
        className={
          fullscreen
            ? 'flex flex-1 flex-col'
            : 'mx-auto flex w-full max-w-6xl flex-1 flex-col px-lg pb-24 pt-md'
        }
      >
        <main className="min-w-0 flex-1">
          <Outlet context={{ revalidate, locationKey: location.key, setFullscreen }} />
        </main>
      </div>

      {!fullscreen && <AboutButton navOffset />}
      {!fullscreen && <AppNavBar />}
    </div>
  )
}

/** Type-safe `useOutletContext` consumers. */
export interface KnowOutletCtx {
  revalidate: () => Promise<void>
  locationKey: string
  setFullscreen: (fullscreen: boolean) => void
  /**
   * A dónde vuelve el boton "Salir" del form focused de `KnowQuestionnaire`.
   * Opcional: acá es `/data`. Lo setea el modo tablet (`kit/KitLayout.tsx`),
   * que monta el mismo form fuera de este layout.
   */
  exitPath?: string
  endSession?: () => void
}
